import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaTimes } from 'react-icons/fa';
import { runLocalAgentAnalysis } from './localAgentService';
import LocalAgentResponseRenderer from './LocalAgentResponseRenderer';
import './AgenticSupport.css';

const AiInlineSetupPanel = ({ isOpen, onClose, moduleKey, scenarios, contextData }) => {
    const [loading, setLoading] = useState(false);
    const [activeResult, setActiveResult] = useState(null);
    const [activeScenarioId, setActiveScenarioId] = useState(null);

    if (!isOpen) return null;

    const handleScenarioClick = async (scenario) => {
        setLoading(true);
        setActiveScenarioId(scenario.id);
        setActiveResult(null);

        try {
            // Mock a slight network delay
            await new Promise(r => setTimeout(r, 800));
            
            const response = await runLocalAgentAnalysis(moduleKey, scenario.id, contextData);
            if (response && response.success) {
                setActiveResult(response);
            }
        } catch (error) {
            console.error("Error running scenario", error);
            setActiveResult("An error occurred during analysis.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="ai-inline-panel mb-4">
            {/* Header */}
            <div className="ai-inline-header d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                    <img src="/img/agentic-ai.png" alt="AI Icon" style={{ width: 22, height: 22, marginRight: 10, filter: 'brightness(0) invert(1)' }} />
                    <h6 className="mb-0 text-white font-weight-bold" style={{ letterSpacing: '1px' }}>KPI SETUP ASSIST</h6>
                    <span className="badge badge-success ml-3 d-flex align-items-center" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#4ade80', border: '1px solid #4ade80' }}>
                        <span className="agentic-pulse-dot mr-2" style={{ width: 6, height: 6 }}></span> AI ENGINE READY
                    </span>
                </div>
                <button type="button" className="ai-inline-close" onClick={onClose}>
                    <FaTimes />
                </button>
            </div>

            {/* Body */}
            <div className="ai-inline-body p-3 bg-white">
                <p className="text-muted small mb-3">Suggestions are FYI only — read and type the value yourself. No field is auto-filled.</p>
                
                {/* Scenario Cards */}
                <div className="row">
                    {scenarios.map((scenario) => {
                        const isActive = activeScenarioId === scenario.id;
                        return (
                            <div className="col-md-4 mb-2" key={scenario.id}>
                                <div 
                                    className={`ai-inline-card p-3 h-100 ${isActive ? 'active' : ''}`}
                                    onClick={() => handleScenarioClick(scenario)}
                                    style={{
                                        border: `1px solid ${isActive ? '#6366f1' : '#e2e8f0'}`,
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        boxShadow: isActive ? '0 0 0 1px #6366f1' : 'none'
                                    }}
                                >
                                    <h6 className="font-weight-bold mb-1" style={{ color: isActive ? '#6366f1' : '#1e293b', fontSize: '14px' }}>
                                        {scenario.label}
                                    </h6>
                                    <span className="text-primary small font-weight-bold text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '10px' }}>
                                        {scenario.description}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Result Box */}
                {(loading || activeResult) && (
                    <div className="ai-inline-result mt-3 p-3" style={{ border: '1px solid #e0e7ff', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
                        {loading ? (
                            <div className="d-flex align-items-center">
                                <div className="spinner-border spinner-border-sm text-primary mr-2" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                                <span className="text-muted small">Analyzing data context...</span>
                            </div>
                        ) : (
                            <div className="ai-result-content la-light-theme">
                                <LocalAgentResponseRenderer response={activeResult} />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

AiInlineSetupPanel.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    moduleKey: PropTypes.string.isRequired,
    scenarios: PropTypes.array.isRequired,
    contextData: PropTypes.object
};

export default AiInlineSetupPanel;
